import "@/App.css";
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import IRRRLCalculator from "./pages/IRRRLCalculator";
import CommissionCalculator from "./pages/CommissionCalculator";
import Dashboard from "./pages/Dashboard";
import LoanManagement from "./pages/LoanManagement";
import Reports from "./pages/Reports";
import SubmissionsReport from "./pages/SubmissionsReport";
import Closings from "./pages/Closings";
import Login from "./pages/Login";
import AdminPanel from "./pages/AdminPanel";
import AuditLog from "./pages/AuditLog";
import { Calculator, LayoutDashboard, FileText, BarChart3, LogOut, User, Home, Shield, ClipboardList, CheckCircle2, KeyRound, ScrollText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import axios from "axios";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Shared Layout with unified navigation
function AppLayout({ children }) {
  const location = useLocation();
  const { user, logout, isMasterAdmin, isAdmin, isProcessor } = useAuth();
  const isCommissionSection = location.pathname.startsWith('/commission');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState({ text: '', isError: false });
  const [pwLoading, setPwLoading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ text: '', isError: false });
    if (pwForm.new !== pwForm.confirm) {
      setPwMsg({ text: 'New passwords do not match', isError: true });
      return;
    }
    if (pwForm.new.length < 4) {
      setPwMsg({ text: 'Password must be at least 4 characters', isError: true });
      return;
    }
    setPwLoading(true);
    try {
      const token = localStorage.getItem('kinfolk_token');
      await axios.put(`${API}/api/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.new
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPwMsg({ text: 'Password updated successfully!', isError: false });
      setPwForm({ current: '', new: '', confirm: '' });
      setTimeout(() => setShowPasswordDialog(false), 1500);
    } catch (err) {
      setPwMsg({ text: err.response?.data?.detail || 'Failed to update password', isError: true });
    } finally {
      setPwLoading(false);
    }
  };

  const getBrokerTypeDisplay = (type) => {
    const types = {
      'equity': 'Equity Partner',
      'founding': 'Founding Broker',
      'executive': 'Executive Broker',
      'senior': 'Senior Broker'
    };
    return types[type] || type;
  };

  const getRoleDisplay = (role) => {
    if (role === 'master_admin') return 'Master Admin';
    if (role === 'admin') return 'Admin';
    if (role === 'processor') return 'Processor';
    return null;
  };
  
  return (
    <div className="min-h-screen bg-black">
      {/* Header Navigation */}
      <header className="bg-black border-b border-[#1a1a1a] sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <NavLink to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_va-loan-savings/artifacts/b3617o7k_image.png" 
                alt="Kinfolk Home Loans" 
                className="h-12 w-auto"
              />
            </NavLink>

            {/* Navigation Tabs */}
            <nav className="flex items-center space-x-1">
              {/* IRRRL Calculator Tab */}
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive && !isCommissionSection
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">IRRRL Calculator</span>
                <span className="sm:hidden">IRRRL</span>
              </NavLink>

              {/* Divider */}
              <div className="h-6 w-px bg-[#333] mx-2"></div>

              {/* Commission Section Tabs */}
              <NavLink
                to="/commission"
                end
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Dashboard</span>
              </NavLink>
              <NavLink
                to="/commission/loans"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <FileText className="w-4 h-4" />
                <span className="hidden md:inline">Loan Management</span>
                <span className="md:hidden">Loans</span>
              </NavLink>
              <NavLink
                to="/commission/calculator"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <Calculator className="w-4 h-4" />
                <span>Calculator</span>
              </NavLink>
              <NavLink
                to="/commission/reports"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <BarChart3 className="w-4 h-4" />
                <span>Reports</span>
              </NavLink>
              <NavLink
                to="/commission/submissions"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <ClipboardList className="w-4 h-4" />
                <span className="hidden lg:inline">Submissions</span>
                <span className="lg:hidden">Subs</span>
              </NavLink>
              <NavLink
                to="/commission/closings"
                className={({ isActive }) =>
                  `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                      : 'text-gray-400 hover:bg-[#111] hover:text-white'
                  }`
                }
              >
                <CheckCircle2 className="w-4 h-4" />
                <span className="hidden lg:inline">Closings</span>
                <span className="lg:hidden">Close</span>
              </NavLink>
              
              {/* Admin Panel - Only for Master Admin */}
              {isMasterAdmin && (
                <>
                  <div className="h-6 w-px bg-[#333] mx-2"></div>
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-amber-900/50 text-amber-400'
                          : 'text-amber-500 hover:bg-amber-900/30 hover:text-amber-400'
                      }`
                    }
                  >
                    <Shield className="w-4 h-4" />
                    <span>Admin</span>
                  </NavLink>
                </>
              )}
              {/* Audit Log - For Admin and Master Admin */}
              {isAdmin && (
                <NavLink
                  to="/audit-log"
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-amber-900/50 text-amber-400'
                        : 'text-amber-500 hover:bg-amber-900/30 hover:text-amber-400'
                    }`
                  }
                  data-testid="nav-audit-log"
                >
                  <ScrollText className="w-4 h-4" />
                  <span>Activity Log</span>
                </NavLink>
              )}
            </nav>

            {/* Right side - User info and logout */}
            <div className="flex items-center space-x-4">
              {user && (
                <button
                  onClick={() => { setPwMsg({ text: '', isError: false }); setPwForm({ current: '', new: '', confirm: '' }); setShowPasswordDialog(true); }}
                  className="hidden sm:flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
                  title="Change Password"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-white">{user.name}</p>
                    <p className="text-xs text-emerald-400">
                      {getRoleDisplay(user.role) || getBrokerTypeDisplay(user.broker_type)}
                    </p>
                  </div>
                  <div className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                </button>
              )}
              <button 
                onClick={logout}
                className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                title="Sign Out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-500" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Update your account password
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-gray-300">Current Password</Label>
              <Input
                type="password"
                value={pwForm.current}
                onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
                required
                data-testid="pw-current"
                className="bg-[#1a1a1a] border-[#333] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">New Password</Label>
              <Input
                type="password"
                value={pwForm.new}
                onChange={(e) => setPwForm({ ...pwForm, new: e.target.value })}
                required
                data-testid="pw-new"
                className="bg-[#1a1a1a] border-[#333] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Confirm New Password</Label>
              <Input
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                required
                data-testid="pw-confirm"
                className="bg-[#1a1a1a] border-[#333] text-white"
              />
            </div>
            {pwMsg.text && (
              <p className={`text-sm ${pwMsg.isError ? 'text-red-400' : 'text-emerald-400'}`}>
                {pwMsg.text}
              </p>
            )}
            <Button
              type="submit"
              disabled={pwLoading}
              data-testid="pw-submit"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {pwLoading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppContent() {
  return (
    <Routes>
      {/* Public route - Login */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout>
            <IRRRLCalculator />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission/loans" element={
        <ProtectedRoute>
          <AppLayout>
            <LoanManagement />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission/calculator" element={
        <ProtectedRoute>
          <AppLayout>
            <CommissionCalculator />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission/reports" element={
        <ProtectedRoute>
          <AppLayout>
            <Reports />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission/submissions" element={
        <ProtectedRoute>
          <AppLayout>
            <SubmissionsReport />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/commission/closings" element={
        <ProtectedRoute>
          <AppLayout>
            <Closings />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AppLayout>
            <AdminPanel />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/audit-log" element={
        <ProtectedRoute>
          <AppLayout>
            <AuditLog />
          </AppLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
